/* ═══════════════════════════════════════════════════════════════
   activityLogger — Fire-and-forget user-activity logger.

   The renderer calls log() at meaningful choke points (login, page
   nav, project/spec/forecast saves, invoice send/paid, exports).
   The server stamps user_id and firm_id from the JWT on receipt;
   clients cannot spoof another user's events.

   Contract:
     - Never throws. A logging failure must never crash the caller.
     - Never blocks. log() returns a promise the caller doesn't await.
     - Coalesces duplicate identical events fired in the same tick
       (e.g. a useEffect that runs twice in StrictMode dev) so the
       table doesn't fill with noise.
     - Drops events when not signed in (no token) — the server would
       reject them anyway, and we don't want a console-error storm
       on the login page.
   ═══════════════════════════════════════════════════════════════ */

import { getAuthToken } from './authService';

import { API_URL } from '../lib/apiBase';

// Coalesce identical events fired within the same micro-task. Useful for
// StrictMode dev where every effect fires twice.
const recent = new Map<string, number>();
const COALESCE_WINDOW_MS = 1500;

let appVersion: string | null = null;
async function getAppVersion(): Promise<string | null> {
  if (appVersion !== null) return appVersion;
  try {
    if (typeof window !== 'undefined' && (window as any).api?.getAppVersion) {
      appVersion = (await (window as any).api.getAppVersion()) || '';
    }
  } catch {
    appVersion = '';
  }
  return appVersion;
}

async function rawPost(body: Record<string, any>): Promise<any> {
  const token = getAuthToken();
  if (!token) throw new Error('Not signed in');
  const r = await fetch(API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({ error: r.statusText }));
    throw new Error(err.error || `API error: ${r.status}`);
  }
  return r.json();
}

export interface ActivityEvent {
  /** Short verb-noun like 'login', 'project.open', 'spec.save'. Free
   *  form but keep stable so reports group correctly. */
  action: string;
  entityType?: string;
  entityId?: string;
  /** Small free-form context. Server clamps serialized size to 4KB. */
  metadata?: Record<string, any>;
}

export async function log(evt: ActivityEvent): Promise<void> {
  if (!evt || !evt.action) return;
  if (!getAuthToken()) return;

  const dedupeKey = `${evt.action}|${evt.entityType || ''}|${evt.entityId || ''}`;
  const now = Date.now();
  const last = recent.get(dedupeKey) || 0;
  if (now - last < COALESCE_WINDOW_MS) return;
  recent.set(dedupeKey, now);
  if (recent.size > 200) {
    const cutoff = now - COALESCE_WINDOW_MS * 4;
    for (const [k, t] of recent) if (t < cutoff) recent.delete(k);
  }

  try {
    const ver = await getAppVersion();
    await rawPost({
      action: 'log_activity',
      eventAction: evt.action,
      entityType: evt.entityType,
      entityId: evt.entityId,
      metadata: evt.metadata,
      appVersion: ver || undefined,
    });
  } catch {
    // Silent — activity logging must never surface to the user.
  }
}

// ─── Convenience helpers ────────────────────────────────────────────

export const logLogin = (memberId?: string) =>
  log({ action: 'login', entityType: 'team_member', entityId: memberId });

export const logLogout = () => log({ action: 'logout' });

export const logPageView = (page: string) => log({ action: 'page.view', metadata: { page } });

export const logEntityAction = (
  action: 'create' | 'update' | 'delete' | 'open',
  entityType: string,
  entityId?: string,
  metadata?: Record<string, any>
) => log({ action: `${entityType}.${action}`, entityType, entityId, metadata });

// ─── Reads (admin viewer) ───────────────────────────────────────────

export interface ActivityRow {
  id: string;
  firmId: string;
  userId: string | null;
  action: string;
  entityType: string | null;
  entityId: string | null;
  metadata: Record<string, any> | null;
  ipAddress: string | null;
  appVersion: string | null;
  createdAt: string;
}

function rowToCamel(row: any): ActivityRow {
  return {
    id: row.id,
    firmId: row.firm_id,
    userId: row.user_id,
    action: row.action,
    entityType: row.entity_type,
    entityId: row.entity_id,
    metadata: row.metadata,
    ipAddress: row.ip_address,
    appVersion: row.app_version,
    createdAt: row.created_at,
  };
}

export async function listActivity(opts?: {
  limit?: number;
  since?: string;
  filterUserId?: string;
  actionFilter?: string;
}): Promise<ActivityRow[]> {
  const body: Record<string, any> = { action: 'list_activity' };
  if (opts?.limit) body.limit = opts.limit;
  if (opts?.since) body.since = opts.since;
  if (opts?.filterUserId) body.filterUserId = opts.filterUserId;
  if (opts?.actionFilter) body.actionFilter = opts.actionFilter;
  const rows: any[] = await rawPost(body);
  return (rows || []).map(rowToCamel);
}

export async function lastSeenPerUser(): Promise<
  { userId: string; action: string; createdAt: string; entityType: string | null; metadata: any }[]
> {
  const rows: any[] = await rawPost({ action: 'last_seen_per_user' });
  return (rows || []).map((r) => ({
    userId: r.user_id,
    action: r.action,
    createdAt: r.created_at,
    entityType: r.entity_type,
    metadata: r.metadata,
  }));
}
